import { lazy } from 'react';
import { Outlet } from 'react-router-dom';

// Icons
import Dashboard from '@/assets/icons/sidebar/dashboard.svg';
import Analytics from '@/assets/icons/sidebar/analytics.svg';
import Intelligence from '@/assets/icons/sidebar/intelligence.svg';
import Ransomware from '@/assets/icons/sidebar/ransomware.svg';
import EASM from '@/assets/icons/sidebar/easm.svg';
import AnalyticsDashboard from '@/assets/icons/sidebar/analytics-dashboard.svg';
import BrandProtection from '@/assets/icons/sidebar/brand-protection.svg';
import Vulnerabilities from '@/assets/icons/sidebar/vulnerabilities.svg';
import SupplyChain from '@/assets/icons/sidebar/supplychainpannel.svg';
import IdentityPanel from '@/assets/icons/sidebar/identity.svg';
import ObservationsIcon from '@/assets/icons/sidebar/threat-intelligence.svg';
import IncidentsIcon from '@/assets/icons/sidebar/alert.svg';

import Catalogs from '@/assets/icons/sidebar/catalogs.svg';
import ThreatActor from '@/assets/icons/sidebar/threat-actor.svg';
import Malwares from '@/assets/icons/sidebar/malwares.svg';
import Attck from '@/assets/icons/sidebar/attck.svg';
import Sources from '@/assets/icons/sidebar/sources.svg';
import Request from '@/assets/icons/sidebar/request.svg';
import DrpSnapshotIcon from '@/assets/icons/sidebar/drp-snapshot.svg';

// Pages
import Page from '@/components/layout/Page';
import RFIPageUpsell from '@/components/content/upsell/RFIPageUpsell';
import Redirect from '@/components/content/redirect/Redirect';
import Search from './features/search/pages/Search';
import UserSettings from '@/features/usersettings/pages/UserSettings';
import Transactions from '@/features/usersettings/pages/Transactions';
import IntelCommonIssue from './features/intelligence/pages/CommonIssue';
import IncidentCommonIssue from './features/incidentmanagement/pages/CommonIssue';
import {
  MonitoringClientLayout,
  MonitoringRedirect,
} from './features/supplychain/components/MonitoringClientLayout';
import { MonitoringSupplierLayout } from './features/supplychain/components/MonitoringSupplierLayout';

// Types
import type { RoutePathDefinition } from '../types/routes';

// Misc
import { mercuryPages } from '../constants/mercuryPages';

import { CATALOGS, MODULES } from '@/constants/permissions';
import { isFeatureEnabled } from '@/utils/featureEnabled';
import { FeatureFlag } from '@/utils/featureFlags';
import Signal from './features/signals/pages/Signal';
import EventView from '@/client/features/events/pages/EventView';
import { LegacyPathRedirect } from './legacyNavRedirects';

const DashboardPage = lazy(() => import('./features/dashboard/pages/Dashboard'));
const NewsPage = lazy(() => import('@/features/news/pages/NewsPage'));

const legacyPathRedirects: Array<{ path: string; from: RegExp; to: string }> = [
  {
    path: 'analytics/intelligence/*',
    from: /^\/analytics\/intelligence(?=\/|$)/,
    to: '/intelligence',
  },
  {
    path: 'analytics/ransomware/*',
    from: /^\/analytics\/ransomware(?=\/|$)/,
    to: '/ransomware',
  },
  {
    path: 'analytics/easm/*',
    from: /^\/analytics\/easm(?=\/|$)/,
    to: '/easm',
  },
  {
    path: 'analytics/vulnerabilities/*',
    from: /^\/analytics\/vulnerabilities(?=\/|$)/,
    to: '/vulnerabilities',
  },
  {
    path: 'analytics/catalogs/*',
    from: /^\/analytics\/catalogs(?=\/|$)/,
    to: '',
  },
  {
    path: 'catalogs/catalogs/*',
    from: /^\/catalogs\/catalogs(?=\/|$)/,
    to: '/catalogs',
  },
];
const IntelligencePage = lazy(() => import('./features/intelligence/pages/Intelligence'));
const RansomwarePage = lazy(() => import('./features/ransomware/pages/Ransomware'));
const EASMPage = lazy(() => import('./features/easm/pages/EASM'));
const AnalyticsPage = lazy(() => import('./features/analyticsdashboard/pages/Analytics'));
const IncidentManagementPage = lazy(
  () => import('./features/incidentmanagement/pages/IncidentManagement')
);
const VulnerabilityIntelligence = lazy(
  () => import('./features/vulnerabilities/pages/VulnerabilitiesAlerts')
);
const SupplyChainPage = lazy(() => import('./features/supplychain/pages/SupplyChain'));
const SupplyChainMonitoringPage = lazy(
  () => import('./features/supplychain/pages/MonitoringOverview')
);
const SupplyChainMonitoringSupplierPage = lazy(
  () => import('./features/supplychain/pages/MonitoringSupplier')
);
const SupplyChainMonitoringIncidentPage = lazy(
  () => import('./features/supplychain/pages/SupplierIncident')
);
const SupplyChainMonitoringRiskAssessmentPage = lazy(
  () => import('./features/supplychain/pages/SupplierRiskAssessment')
);
const Details = lazy(() => import('./features/supplychain/pages/Supplier'));

// Read at module load — flipping the flag in the Experimental Features modal
// requires a page refresh for the new nav entry to appear (same model as the
// other flag-gated views like ShowNewEventsUi).
const supplyChainMonitoringEnabled = isFeatureEnabled(FeatureFlag.SupplyChainMonitoring);
const analystToolsEnabled = isFeatureEnabled(FeatureFlag.AnalystTools);
const drpSnapshotEnabled = isFeatureEnabled(FeatureFlag.DrpSnapshot);
const DrpSnapshotListPage = lazy(() => import('./features/drpsnapshot/pages/DrpSnapshotList'));
const DrpSnapshotReportPage = lazy(() => import('./features/drpsnapshot/pages/DrpSnapshotReport'));
const IdentityPanelPage = lazy(() => import('./features/identitypanel/pages/IdentityPanel'));
const ObservationsPage = lazy(() => import('./features/observations/pages/Observations'));
const ObservationDetailPage = lazy(() => import('./features/observations/pages/ObservationDetail'));
const CreateObservationPage = lazy(() => import('./features/observations/pages/CreateObservation'));
const IncidentsPage = lazy(() => import('./features/incidents/pages/Incidents'));
const IncidentDetailPage = lazy(() => import('./features/incidents/pages/IncidentDetail'));

const RawIssue = lazy(() => import('./features/intelligence/pages/RawIssue'));
const FinishedIssue = lazy(() => import('./features/intelligence/pages/FinishedIssue'));
const IncidentIssue = lazy(() => import('./features/incidentmanagement/pages/IncidentIssue'));
const TakedownIssue = lazy(() => import('./features/incidentmanagement/pages/TakedownIssue'));
const VulnerabilitiesIssue = lazy(() => import('./features/vulnerabilities/pages/Issue'));
const IdentityPage = lazy(() => import('./features/identitypanel/pages/Identity'));

const ThreatActors = lazy(
  () => import('./features/catalogs/features/threatactors/pages/ThreatActors')
);
const ThreatActorEntity = lazy(
  () => import('./features/catalogs/features/threatactors/pages/Entity')
);
const MalwaresPage = lazy(() => import('./features/catalogs/features/malwares/pages/MalwareKits'));
const MalwaresEntity = lazy(() => import('./features/catalogs/features/malwares/pages/Entity'));
const AttCkPage = lazy(() => import('./features/catalogs/features/attck/pages/Attck'));
const AttckEntity = lazy(() => import('./features/catalogs/features/attck/pages/Entity'));
const SourcesPage = lazy(() => import('./features/catalogs/features/sources/pages/Sources'));
const SourceEntity = lazy(() => import('./features/catalogs/features/sources/pages/Entity'));
const VulnerabilitiesCatalogPage = lazy(
  () => import('./features/catalogs/features/vulnerabilities/pages/Vulnerabilities')
);
const VulnerabilitiesEntity = lazy(
  () => import('./features/catalogs/features/vulnerabilities/pages/Entity')
);

const ServiceRequest = lazy(() => import('./features/servicerequests/pages/ServiceRequests'));
const ServiceRequestsIssue = lazy(() => import('./features/servicerequests/pages/RFIIssue'));

export const clientItems: RoutePathDefinition[] = [
  {
    path: '/',
    title: 'Dashboard',
    index: true,
    element: (
      <Page>
        <DashboardPage />
      </Page>
    ),
    icon: Dashboard,
    nav: true,
  },
  {
    path: 'analytics',
    isSubMenu: true,
    layout: true,
    name: 'Analytics',
    icon: Analytics,
    children: [
      {
        path: 'intelligence',
        element: <Outlet />,
        children: [
          {
            path: '',
            page: mercuryPages.intelligence.id,
            index: true,
            element: (
              <Page documentTitle={mercuryPages.intelligence.title}>
                <IntelligencePage />
              </Page>
            ),
            getPageInfo: true,
            icon: Intelligence,
            nav: true,
          },
          {
            path: 'issue/:issue',
            element: (
              <Page>
                <IntelCommonIssue />
              </Page>
            ),
          },
          {
            path: 'finished/issue/:issue',
            aliases: ['finished_intel/:issue'],
            element: (
              <Page>
                <FinishedIssue />
              </Page>
            ),
          },
          {
            path: 'raw/issue/:issue',
            aliases: ['raw_intel/:issue'],
            element: (
              <Page>
                <RawIssue />
              </Page>
            ),
          },
        ],
      },
      {
        path: 'ransomware',
        getPageInfo: true,
        page: mercuryPages.ransomware.id,
        icon: Ransomware,
        nav: true,
        element: (
          <RFIPageUpsell
            requiredModules={MODULES.ransomware.modules}
            documentTitle={mercuryPages.ransomware.title}
          >
            <RansomwarePage />
          </RFIPageUpsell>
        ),
      },
      {
        path: 'easm',
        hideOnNoAccess: true,
        page: mercuryPages.easm.id,
        getPageInfo: true,
        icon: EASM,
        nav: true,
        element: (
          <Page requiredModules={MODULES.easm.modules} documentTitle={mercuryPages.easm.title}>
            <EASMPage />
          </Page>
        ),
      },
      {
        path: 'analytics',
        hideOnNoAccess: true,
        page: mercuryPages.analytics.id,
        getPageInfo: true,
        icon: AnalyticsDashboard,
        nav: true,
        element: (
          <Page
            requiredModules={MODULES.analyticsdashboard.modules}
            documentTitle={mercuryPages.analytics.title}
          >
            <AnalyticsPage />
          </Page>
        ),
      },
    ],
  },
  {
    path: 'drp',
    element: <Redirect to="/incidentmanagement" />,
    nav: false,
  },
  ...legacyPathRedirects.map(({ path, from, to }) => ({
    path,
    element: <LegacyPathRedirect from={from} to={to} />,
    nav: false,
  })),
  {
    path: 'drp/issue/:issue',
    element: <Redirect to="/incidentmanagement/issue/:issue" />,
    nav: false,
  },
  {
    path: 'drp/alert/issue/:issue',
    element: <Redirect to="/incidentmanagement/alert/issue/:issue" />,
    nav: false,
  },
  {
    path: 'drp/takedown/issue/:issue',
    element: <Redirect to="/incidentmanagement/takedown/issue/:issue" />,
    nav: false,
  },
  {
    path: 'incidentmanagement',
    element: <Outlet />,
    children: [
      {
        path: '',
        page: mercuryPages.incidentmanagement.id,
        index: true,
        element: (
          <Page documentTitle={mercuryPages.incidentmanagement.title} withOutlet>
            <IncidentManagementPage />
          </Page>
        ),
        getPageInfo: true,
        icon: BrandProtection,
        nav: true,
      },
      {
        path: 'issue/:issue',
        element: (
          <Page>
            <IncidentCommonIssue />
          </Page>
        ),
      },
      {
        path: 'alert/issue/:issue',
        aliases: ['incident/:issue'],
        element: (
          <Page>
            <IncidentIssue />
          </Page>
        ),
      },
      {
        path: 'takedown/issue/:issue',
        aliases: ['takedown/:issue'],
        element: (
          <Page>
            <TakedownIssue />
          </Page>
        ),
      },
    ],
  },
  {
    path: 'vulnerabilities',
    element: <Outlet />,
    children: [
      {
        path: '',
        page: mercuryPages.vulnerabilitiesInt.id,
        index: true,
        element: (
          <Page documentTitle={mercuryPages.vulnerabilitiesInt.title}>
            <VulnerabilityIntelligence />
          </Page>
        ),
        getPageInfo: true,
        icon: Vulnerabilities,
        nav: true,
      },
      {
        path: 'alert/issue/:issue',
        element: (
          <Page>
            <VulnerabilitiesIssue />
          </Page>
        ),
      },
    ],
  },
  {
    path: 'supplychain',
    element: <Outlet />,
    children: [
      {
        path: '',
        page: mercuryPages.supplychain.id,
        index: true,
        hideOnNoAccess: true,
        element: (
          <Page
            documentTitle={mercuryPages.supplychain.title}
            requiredModules={MODULES.supplychain.modules}
            withOutlet
          >
            <SupplyChainPage />
          </Page>
        ),
        getPageInfo: true,
        icon: SupplyChain,
        nav: true,
      },
      ...(supplyChainMonitoringEnabled
        ? [
            {
              path: 'monitoring',
              page: mercuryPages.supplychainMonitoring.id,
              hideOnNoAccess: true,
              element: (
                <Page
                  documentTitle={mercuryPages.supplychainMonitoring.title}
                  requiredModules={MODULES.supplychain.modules}
                >
                  <MonitoringRedirect />
                </Page>
              ),
              getPageInfo: true,
              icon: SupplyChain,
              nav: true,
            },
            {
              path: 'monitoring/:client',
              hideOnNoAccess: true,
              element: (
                <Page
                  documentTitle={mercuryPages.supplychainMonitoring.title}
                  requiredModules={MODULES.supplychain.modules}
                >
                  <MonitoringClientLayout />
                </Page>
              ),
              children: [
                {
                  path: '',
                  index: true,
                  element: <SupplyChainMonitoringPage />,
                },
                {
                  path: 'supplier/:supplier_id',
                  element: <MonitoringSupplierLayout />,
                  children: [
                    {
                      path: '',
                      index: true,
                      element: <SupplyChainMonitoringSupplierPage />,
                    },
                    {
                      path: 'incident/:incident_id',
                      element: <SupplyChainMonitoringIncidentPage />,
                    },
                    {
                      path: 'risk-assessment/:assessment_id',
                      element: <SupplyChainMonitoringRiskAssessmentPage />,
                    },
                  ],
                },
              ],
            },
          ]
        : []),
      {
        path: 'supplier/:supplier_id/:supplier_name',
        element: (
          <Page>
            <Details />
          </Page>
        ),
      },
    ],
  },
  {
    path: 'identitypanel',
    element: <Outlet />,
    children: [
      {
        path: '',
        page: mercuryPages.identitypanel.id,
        hideOnNoAccess: true,
        index: true,
        element: (
          <Page
            requiredModules={MODULES.identitypanel.modules}
            documentTitle={mercuryPages.identitypanel.title}
          >
            <IdentityPanelPage />
          </Page>
        ),
        getPageInfo: true,
        icon: IdentityPanel,
        nav: true,
      },
      {
        path: 'entity/:identity',
        element: (
          <Page requiredModules={MODULES.identitypanel.modules}>
            <IdentityPage />
          </Page>
        ),
      },
    ],
  },
  {
    path: 'catalogs',
    isSubMenu: true,
    layout: true,
    name: 'Catalogs',
    icon: Catalogs,
    children: [
      {
        path: 'catalogs/threatactors',
        element: <Outlet />,
        children: [
          {
            path: '',
            page: mercuryPages.threatactors.id,
            index: true,
            element: (
              <RFIPageUpsell
                requiredModules={CATALOGS.threatactors.modules}
                title={mercuryPages.threatactors.title}
              >
                <ThreatActors />
              </RFIPageUpsell>
            ),
            getPageInfo: true,
            icon: ThreatActor,
            nav: true,
          },
          {
            path: 'entity/:entity',
            aliases: ['threat_actor/:entity', 'provider/:entity', 'persona/:entity'], // keep the original persona alias for link stability
            element: (
              <RFIPageUpsell requiredModules={CATALOGS.threatactors.modules}>
                <ThreatActorEntity />
              </RFIPageUpsell>
            ),
          },
        ],
      },
      {
        path: 'catalogs/malwarekits',
        element: <Outlet />,
        children: [
          {
            path: '',
            page: mercuryPages.malwarekits.id,
            index: true,
            element: (
              <RFIPageUpsell
                requiredModules={CATALOGS.malwarekits.modules}
                title={mercuryPages.malwarekits.title}
              >
                <MalwaresPage />
              </RFIPageUpsell>
            ),
            getPageInfo: true,
            icon: Malwares,
            nav: true,
          },
          {
            path: 'entity/:entity',
            aliases: ['malware_kit/:entity'],
            element: (
              <RFIPageUpsell requiredModules={CATALOGS.malwarekits.modules}>
                <MalwaresEntity />
              </RFIPageUpsell>
            ),
          },
        ],
      },
      {
        path: 'catalogs/attck',
        element: <Outlet />,
        children: [
          {
            path: '',
            page: mercuryPages.attcks.id,
            index: true,
            element: (
              <RFIPageUpsell
                requiredModules={CATALOGS.attcks.modules}
                title={mercuryPages.attcks.title}
              >
                <AttCkPage />
              </RFIPageUpsell>
            ),
            getPageInfo: true,
            icon: Attck,
            nav: true,
          },
          {
            path: 'entity/:entity',
            aliases: ['attck/:entity'],
            element: (
              <RFIPageUpsell requiredModules={CATALOGS.attcks.modules}>
                <AttckEntity />
              </RFIPageUpsell>
            ),
          },
        ],
      },
      {
        path: 'catalogs/sources',
        element: <Outlet />,
        children: [
          {
            path: '',
            page: mercuryPages.sources.id,
            index: true,
            element: (
              <RFIPageUpsell
                requiredModules={CATALOGS.sources.modules}
                title={mercuryPages.sources.title}
              >
                <SourcesPage />
              </RFIPageUpsell>
            ),
            getPageInfo: true,
            icon: Sources,
            nav: true,
          },
          {
            path: 'entity/:entity',
            aliases: ['source/:entity'],
            element: (
              <RFIPageUpsell requiredModules={CATALOGS.sources.modules}>
                <SourceEntity />
              </RFIPageUpsell>
            ),
          },
        ],
      },
      {
        path: 'catalogs/vulnerabilities',
        element: <Outlet />,
        children: [
          {
            path: '',
            page: mercuryPages.vulnerabilitiesCat.id,
            index: true,
            element: (
              <RFIPageUpsell
                requiredModules={CATALOGS.vulnerabilities.modules}
                title={mercuryPages.vulnerabilitiesCat.title}
              >
                <VulnerabilitiesCatalogPage />
              </RFIPageUpsell>
            ),
            getPageInfo: true,
            icon: Vulnerabilities,
            nav: true,
          },
          {
            path: 'entity/:entity',
            aliases: ['vulnerability/:entity'],
            element: (
              <RFIPageUpsell requiredModules={CATALOGS.vulnerabilities.modules}>
                <VulnerabilitiesEntity />
              </RFIPageUpsell>
            ),
          },
        ],
      },
    ],
  },
  ...(drpSnapshotEnabled
    ? [
        {
          path: 'drp-snapshots',
          element: <Outlet />,
          children: [
            {
              path: '',
              index: true,
              page: mercuryPages.drpSnapshot.id,
              getPageInfo: true,
              icon: DrpSnapshotIcon,
              nav: true,
              element: <DrpSnapshotListPage />,
            },
            {
              path: ':id/report',
              element: <DrpSnapshotReportPage />,
            },
          ],
        },
      ]
    : []),
  {
    path: 'servicerequests',
    element: <Outlet />,
    children: [
      {
        path: '',
        page: mercuryPages.servicerequests.id,
        title: 'Service Request',
        index: true,
        element: (
          <Page documentTitle={mercuryPages.servicerequests.title}>
            <ServiceRequest />
          </Page>
        ),
        icon: Request,
        getPageInfo: true,
        nav: true,
      },
      {
        path: 'alert/issue/:issue',
        element: (
          <Page>
            <ServiceRequestsIssue />
          </Page>
        ),
      },
      {
        path: 'takedown/issue/:issue',
        element: (
          <Page>
            <TakedownIssue />
          </Page>
        ),
      },
    ],
  },
  ...(analystToolsEnabled
    ? [
        {
          path: 'analyst-tools',
          isSubMenu: true,
          layout: true,
          name: 'Analyst Tools',
          icon: ObservationsIcon,
          page: mercuryPages.analysttools.id,
          getPageInfo: true,
          hideOnNoAccess: true,
          children: [
            {
              path: 'analyst/observations',
              element: <Outlet />,
              children: [
                {
                  path: '',
                  index: true,
                  page: mercuryPages.observations.id,
                  hideOnNoAccess: true,
                  getPageInfo: true,
                  icon: Analytics,
                  nav: true,
                  element: (
                    <Page
                      allowedRoles={mercuryPages.observations.permissions?.roles}
                      documentTitle={mercuryPages.observations.title}
                    >
                      <ObservationsPage />
                    </Page>
                  ),
                },
                {
                  path: 'new',
                  element: (
                    <Page allowedRoles={mercuryPages.observations.permissions?.roles}>
                      <CreateObservationPage />
                    </Page>
                  ),
                },
                {
                  path: ':issue',
                  element: (
                    <Page allowedRoles={mercuryPages.observations.permissions?.roles}>
                      <ObservationDetailPage />
                    </Page>
                  ),
                },
              ],
            },
            {
              path: 'analyst/incidents',
              element: <Outlet />,
              children: [
                {
                  path: '',
                  index: true,
                  page: mercuryPages.incidents.id,
                  hideOnNoAccess: true,
                  getPageInfo: true,
                  icon: IncidentsIcon,
                  nav: true,
                  element: (
                    <Page
                      allowedRoles={mercuryPages.incidents.permissions?.roles}
                      documentTitle={mercuryPages.incidents.title}
                    >
                      <IncidentsPage />
                    </Page>
                  ),
                },
                {
                  path: ':issue',
                  element: (
                    <Page
                      allowedRoles={mercuryPages.incidents.permissions?.roles}
                      documentTitle={mercuryPages.incidents.title}
                    >
                      <IncidentDetailPage />
                    </Page>
                  ),
                },
              ],
            },
          ],
        },
        {
          path: 'observations',
          element: <Redirect to="/analyst/observations" />,
          nav: false,
        },
        {
          path: 'observations/:issue',
          element: <Redirect to="/analyst/observations/:issue" />,
          nav: false,
        },
      ]
    : []),
  {
    path: 'settings',
    element: (
      <Page>
        <UserSettings />
      </Page>
    ),
    nav: false,
  },
  {
    path: 'transactions',
    element: (
      <Page documentTitle="My Transactions">
        <Transactions />
      </Page>
    ),
    nav: false,
  },
  {
    path: 'news',
    element: (
      <Page documentTitle="Mercury Updates">
        <NewsPage />
      </Page>
    ),
    nav: false,
  },
  {
    path: 'search',
    element: (
      <Page>
        <Search />
      </Page>
    ),
    nav: false,
  },
  {
    path: 'technology/:entity',
    element: <Redirect to="/search" search="q=technologies.id:" />,
    nav: false,
  },
  {
    path: 'industry/:entity',
    element: <Redirect to="/search" search="q=industries.id:" />,
    nav: false,
  },
  {
    path: 'location/:entity',
    element: <Redirect to="/search" search="q=locations.id:" />,
    nav: false,
  },
  {
    path: 'company/:entity',
    element: <Redirect to="/search" search="q=companies.id:" />,
    nav: false,
  },
  {
    path: 'signal/:entity',
    element: <Signal />,
    nav: false,
  },
  {
    path: 'cluster/:entity',
    element: (
      <Redirect
        to="/analytics"
        search={(entity) => {
          const id = entity?.startsWith('urn') ? entity.split(':')[2] : entity;
          return `?query[raw]=id:${id}`;
        }}
      />
    ),
    nav: false,
  },
  {
    path: 'events/:entity',
    element: <EventView />,
    nav: false,
  },
];
